/**
 * Pure builders for adding/removing entries in repeatable array fields of the
 * medical PA form. They recompute the order data and the re-indexed form
 * configuration without touching React state.
 *
 * @module medicalPaForm/components/utils/formArrayMutations
 */
import { LatestMedicalPaFormData } from "../../../../api/getCalls/getLatestMedicalPaFormData";
import { FormDataModel } from "../../../../data-model/pharmaPaFormModel";
import {
  createNestedObjectFromDotKeys,
  getArrayChildren,
} from "../../../../utils/createObjectFromDotNotation";
import {
  deleteNestedValue,
  setNestedValue,
} from "../../../../utils/getNestedValue";
import { replaceKeyOfArrayTypeFieldWithCorrectIndex } from "../../../../utils/replaceKeyOfArrayTypeFieldWithCorrectIndex";
import { createOrderDataFromContextFormData } from "./createOrderDataFromContextFormData";

/** Result of an array-field mutation: the new order data and form config. */
export interface FormArrayMutationResult {
  /** Re-indexed form configuration reflecting the mutated array. */
  updatedFormConfig: FormDataModel;
  /** Order data after the array entry was added/removed. */
  currentDataInMedicalPaOrder: any;
}

/** Empty form-config fallback used when no config has resolved yet. */
const EMPTY_FORM_CONFIG: FormDataModel = { fields: [], formTitle: "" };

/**
 * Appends a blank entry to the repeatable array field identified by `key`.
 *
 * @param formFieldsData - Current context field values.
 * @param latestMedicalPaFormData - Latest saved form data.
 * @param formConfig - Current resolved form configuration.
 * @param key - Array-field key to append to.
 * @returns The updated form config and order data.
 */
export const buildAddMoreResult = (
  formFieldsData: Record<string, any>,
  latestMedicalPaFormData: LatestMedicalPaFormData,
  formConfig: FormDataModel | null,
  key: string,
): FormArrayMutationResult => {
  const data = createOrderDataFromContextFormData(
    formFieldsData,
    latestMedicalPaFormData,
  );
  const keys = getArrayChildren(Object.keys(formFieldsData), key);
  const objectWithKeysAndValues = keys?.reduce((acc, key) => {
    acc[key] = formFieldsData[key].filledValue;
    return acc;
  }, {});
  const oldUpdatedData = createNestedObjectFromDotKeys(
    objectWithKeysAndValues ?? {},
  );
  const updatedData = [...oldUpdatedData[key], {}];
  const currentDataInMedicalPaOrder = setNestedValue(
    data,
    key,
    updatedData,
    true,
  );
  const updatedFormConfig = replaceKeyOfArrayTypeFieldWithCorrectIndex(
    formConfig ?? EMPTY_FORM_CONFIG,
    currentDataInMedicalPaOrder,
  );

  return { updatedFormConfig, currentDataInMedicalPaOrder };
};

/**
 * Removes the entry at `path` from the repeatable array field `parentKey`.
 *
 * @param formFieldsData - Current context field values.
 * @param latestMedicalPaFormData - Latest saved form data.
 * @param formConfig - Current resolved form configuration.
 * @param path - Full path of the entry to remove.
 * @param parentKey - Array-field key that owns the entry.
 * @returns The updated form config and order data.
 */
export const buildDeleteResult = (
  formFieldsData: Record<string, any>,
  latestMedicalPaFormData: LatestMedicalPaFormData,
  formConfig: FormDataModel | null,
  path: string,
  parentKey: string,
): FormArrayMutationResult => {
  let data = createOrderDataFromContextFormData(
    formFieldsData,
    latestMedicalPaFormData,
  );
  const keys = getArrayChildren(Object.keys(formFieldsData), parentKey);
  const objectWithKeysAndValues = keys?.reduce((acc, key) => {
    acc[key] = formFieldsData[key].filledValue;
    return acc;
  }, {});
  const oldUpdatedData = createNestedObjectFromDotKeys(
    objectWithKeysAndValues ?? {},
  );
  data = setNestedValue(data, parentKey, oldUpdatedData[parentKey], true);
  const currentDataInMedicalPaOrder = deleteNestedValue(data, path);
  const updatedFormConfig = replaceKeyOfArrayTypeFieldWithCorrectIndex(
    formConfig ?? EMPTY_FORM_CONFIG,
    currentDataInMedicalPaOrder,
  );

  return { updatedFormConfig, currentDataInMedicalPaOrder };
};
